<template>
  <div
    class="mini-cart"
    :class="{
      'mini-cart--opened': isMiniCartShowing && orderIsInitialized,
    }"
    :style="transformStyle"
    @mouseenter="$store.dispatch('nav/setAdditionalNav', 'mini-cart')"
  >
    <!-- Flash message -->
    <FlashMessages :context="$CONTEXTS.miniCart" class="mini-cart__flash"></FlashMessages>
    <!-- Header -->
    <div class="mini-cart__title">
      <div class="mini-cart__cart-items">
        <span class="icon-bag"></span>
        <span class="mini-cart__cart-items-counter">{{ itemsCount }}</span>
      </div>
      <span>{{ $t('title') }}</span>
      <span class="mini-cart__close icon-cross" @click="closeAdditionalNav"></span>
    </div>
    <!-- LoadingIndicator -->
    <div class="mini-cart__loader" :class="{ 'mini-cart__loader--loading': cartItemIsLoading }">
      <LoadingIndicator class="mini-cart__loader-image" />
    </div>
    <!-- Empty cart container  -->
    <transition name="toggle-fade">
      <div
        v-show="!itemsLength && !quickAdding"
        ref="emptyContainer"
        class="mini-cart__container mini-cart__container--empty"
        @click="$store.dispatch('nav/setAdditionalNav', '')"
      >
        <EmptyCart :homepageUrl="homepageUrl" />
        <div ref="deliveryNotice" class="mini-cart__delivery-notice">
          <div class="mini-cart__delivery-icon icon-course"></div>
          {{ deliveryNotice }}
        </div>
      </div>
    </transition>
    <!-- Cart container with items -->
    <transition name="toggle-fade">
      <div
        v-show="itemsLength"
        ref="itemsContainer"
        class="mini-cart__container mini-cart__container--filled"
      >
        <!-- NoticeMessage -->
        <NoticeMessage context="mini-cart" class="mini-cart__notice"></NoticeMessage>
        <!-- Cart items -->
        <div ref="cartItems" class="mini-cart__items">
          <OrderItem
            v-for="(item, index) of items"
            :id="Number(item.variantId)"
            :key="item.variantId"
            :isGiftCardOrder="isGiftCardOrder"
            :lineItemId="Number(item.id)"
            :class="{
              'order-item--first': index === 0,
              'order-item--transitioning': itemIsGettingAdded,
              'order-item--merging': mergedItemIndex === index,
            }"
            :item="item"
            :index="index"
            :quantity="item.quantity"
            :stock="item.stock"
            :onCartContentChange="onCartContentChange"
            :flashContext="$CONTEXTS.miniCart"
          />
        </div>
        <!-- Promocode component -->
        <template>
          <PromoAccordion v-if="!adjustmentIsLoading" ref="promoAccordion">
            <template slot="promo">
              <PromoCode :variant="VARIANTS.tall" :flashContext="$CONTEXTS.miniCart" />
            </template>
          </PromoAccordion>
        </template>
        <!-- Total cost -->
        <div class="mini-cart__total-box">
          <div v-if="adjustmentIsLoading" class="mini-cart__loader mini-cart__loader--loading">
            <LoadingIndicator class="mini-cart__loader-image" />
          </div>
          <div v-else>
            <OrderTotals :rows="orderTotalsRows" />
          </div>
        </div>
        <!-- Delivery info -->
        <div v-if="isDeliveryNoticeShown" ref="deliveryNotice" class="mini-cart__delivery-notice">
          <div class="mini-cart__delivery-icon icon-course"></div>
          {{ deliveryNotice }}
        </div>
        <Recommendations v-if="shouldShowRecommendations" />
      </div>
    </transition>
    <!-- Bottom box -->
    <div v-show="isBottomBoxShowing" class="mini-cart__bottom-box">
      <!-- Checkout Buttons -->
      <div class="mini-cart__checkout">
        <Button
          :href="homepageUrl + '/cart'"
          tag="a"
          :isSubmitting="isSubmitting"
          class="mini-cart__cta"
          @click.prevent="redirectToCheckout"
          >{{ $t('global.checkout') }}</Button
        >
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { EventBus, EVENTS } from '@/services/event-bus'
import { onLoad } from '@/helpers/event'
import cartMixin from '@/mixins/cart'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'
import Button from '@/components/_shared/Button.vue'
import FlashMessages from '@/components/_shared/FlashMessage/FlashMessages.vue'
import NoticeMessage from '@/components/_shared/NoticeMessage/NoticeMessage.vue'
import Recommendations from './Recommendations.vue'
import OrderItem from '@/components/_shared/Order/Item/OrderItem.vue'
import OrderTotals, { ORDER_TOTALS_ROWS } from '@/components/_shared/Order/Totals/OrderTotals.vue'
import PromoAccordion from '@/components/_shared/Promo/PromoAccordion.vue'
import PromoCode from '@/components/_shared/Promo/PromoCode.vue'
import { VARIANTS } from '@/components/_shared/Form/form-helper'
import EmptyCart from './EmptyCart/EmptyCart.vue'

export default {
  name: 'MiniCart',
  components: {
    Button,
    EmptyCart,
    FlashMessages,
    LoadingIndicator,
    NoticeMessage,
    OrderItem,
    OrderTotals,
    PromoAccordion,
    PromoCode,
    Recommendations,
  },
  mixins: [cartMixin],

  data() {
    return {
      scrollbarWidth: this.$store.state.page.scrollbarWidth,
      ORDER_TOTALS_ROWS,
      VARIANTS,
    }
  },

  computed: {
    ...mapGetters({
      overlayIsOpen: 'nav/overlayIsOpen',
      currentAdditionalNav: 'nav/currentAdditionalNav',
      deliveryNotice: 'nav/deliveryNotice',
      isGiftCardOrder: 'order/isGiftCardOrder',
      giftCardsTotal: 'order/giftCardsTotal',
    }),

    shouldShowRecommendations() {
      return (
        this.isMiniCartShowing &&
        this.orderIsInitialized &&
        this.itemsLength &&
        !this.isPreorder &&
        !this.isGiftCardOrder
      )
    },

    orderTotalsRows() {
      return [
        {
          key: ORDER_TOTALS_ROWS.giftCards,
          value: this.giftCardsTotal,
          condition: this.giftCardsTotal !== 0,
        },
        { key: ORDER_TOTALS_ROWS.shipping, value: this.shippingTotal },
        {
          key: ORDER_TOTALS_ROWS.usedStoreCredit,
          value: this.usedStoreCredit,
          condition: this.isStoreCreditUsed,
        },
        { key: ORDER_TOTALS_ROWS.total, value: this.total },
        {
          key: ORDER_TOTALS_ROWS.remainingStoreCredit,
          value: this.remainingStoreCredit,
          condition: this.isStoreCreditUsed,
        },
      ]
    },

    isMiniCartShowing() {
      return this.currentAdditionalNav === 'mini-cart'
    },

    transformStyle() {
      const transformShift = this.$store.state.nav.overlayIsOpen ? 0 : this.scrollbarWidth
      return `margin-right: -${transformShift}px;`
    },

    isBottomBoxShowing() {
      return !!this.itemsLength
    },

    isDeliveryNoticeShown() {
      return !this.isPreorder && !this.isCyclon
    },
  },

  watch: {
    currentAdditionalNav() {
      // Helper for preventing address and bottom bar to appear/disappear when overlay is open. For mobile vps only.
      if (this.isMiniCartShowing) {
        On.initMobileScrollHelper(this.$refs.itemsContainer)
        On.initMobileScrollHelper(this.$refs.emptyContainer)
        EventBus.$emit(EVENTS.minicart.toggle, { isOpen: true })
      } else {
        On.destroyMobileScrollHelper(this.$refs.itemsContainer)
        On.destroyMobileScrollHelper(this.$refs.emptyContainer)
        EventBus.$emit(EVENTS.minicart.toggle, { isOpen: false })
      }
    },

    overlayIsOpen() {
      if (!this.overlayIsOpen)
        setTimeout(() => {
          const itemsContainer = this.$refs.itemsContainer
          if (itemsContainer !== undefined) itemsContainer.scrollTop = 0
        }, On.TransitionTimes.slow)
    },
  },

  mounted() {
    onLoad().then(this.fetchData)
  },

  methods: {
    fetchData() {
      this.$store.dispatch('store/fetchData')
      this.$store.dispatch('order/fetchData')
    },

    closeAdditionalNav() {
      this.$store.dispatch('nav/setAdditionalNav', '')
    },
  },
}
</script>

<style lang="scss" scoped>
@import './MiniCart';
</style>

<i18n src="@/locales/minicart.json">
</i18n>
